import { useStaleSWRConfig } from '@solinkcorp/utils/hooks';
import { useState } from 'react';
import useSWRSubscription from 'swr/subscription';

export interface SSEOptions {
  url: string;
  accessToken: string;
  withCredentials?: boolean;
  onMessage?: (data: string) => void;
  onError?: (error: Event) => void;
  onOpen?: (event: Event) => void;
}

export interface SSEConnectionState {
  status: 'connecting' | 'connected' | 'disconnected' | 'error';
  events: string[];
  error?: Event;
}

export function useServerSentEvents(options: SSEOptions): SSEConnectionState {
  const { url, accessToken, withCredentials = true, onMessage, onError, onOpen } = options;
  const [status, setStatus] = useState<SSEConnectionState['status']>('disconnected');

  const swrKey = url && accessToken ? `sse-${url}` : null;

  const staleSWRConfig = useStaleSWRConfig(swrKey);

  const { data: events, error } = useSWRSubscription(
    swrKey,
    (_key, { next }) => {
      setStatus('connecting');

      const urlWithToken = new URL(url);
      urlWithToken.searchParams.set('jwt', accessToken);

      const eventSource = new EventSource(urlWithToken.toString(), {
        withCredentials,
      });

      eventSource.onopen = (event) => {
        setStatus('connected');
        onOpen?.(event);
      };

      eventSource.onmessage = (event) => {
        next(null, (prevEvents: string[] = []) => [...prevEvents, event.data]);
        onMessage?.(event.data);
      };

      eventSource.onerror = (errorEvent) => {
        setStatus('error');
        onError?.(errorEvent);
        next(errorEvent);
      };

      return () => {
        eventSource.close();
        setStatus('disconnected');
      };
    },
    {
      ...staleSWRConfig,
      fallbackData: [],
    },
  );

  return {
    status,
    events,
    error,
  };
}
